"use client";

import { motion } from "framer-motion";
import Link from "next/link";
import { ArrowDown } from "lucide-react";

const images = [
  "ARCHITECTURAL.webp",
  "INTERIORd.webp",
  "TURNKEY.webp",
  "LANDSCAPE.webp",
  "LIGHTING.webp",
  "FURNITURE.webp",
];

const services = [
  {
    title: "Architecture Consultation",
    description:
      "Innovative architectural solutions blending modern design with Vastu-aligned spatial planning for balanced, harmonious environments.",
    alt: "Modern architectural interior with Vastu principles applied",
  },
  {
    title: "Interior Design & Vastu Consultation",
    description:
      "Elegant, curated indoor spaces crafted for comfort and style, integrated with Vastu principles to ensure positive energy flow.",
    alt: "Warm, stylish indoor living room designed with Vastu balance",
  },
  {
    title: "Turnkey Execution",
    description:
      "Complete project delivery from start to finish, seamlessly integrating design and construction for flawless execution.",
    alt: "Modern building and landscaped garden showcasing turnkey project",
  },
  {
    title: "Landscape Design",
    description:
      "Beautifully designed indoor and outdoor green spaces, harmonizing nature with your living environment.",
    alt: "Indoor-outdoor courtyard blending natural greenery with architecture",
  },
  {
    title: "Lighting Design",
    description:
      "Customized ambient and task lighting solutions that enhance the mood and functionality of every space.",
    alt: "Warm ambient lighting creating cozy atmosphere in modern interior",
  },
  {
    title: "Furniture Design",
    description:
      "Custom-crafted furniture pieces that perfectly complement your style and optimize space usage.",
    alt: "Elegant designer furniture with stylish décor details",
  },
];

const Services = () => {
  return (
    <section id="services" className="min-h-full bg-white px-6 py-16">
      <div className="max-w-7xl mx-auto text-center">
        <h2 className="text-4xl sm:text-5xl font-serif text-[#A65D5D] mb-10">
          From Concept to Creation: Our Luxe Services
        </h2>

        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{
                duration: 0.1,
                delay: index * 0.1,
                ease: "easeOut",
              }}
              className="bg-secondaryColor/40 shadow-md rounded-lg p-6 flex flex-col items-center text-center"
            >
              <img
                src={images[index]}
                alt={service.alt}
                loading="lazy"
                className="w-fit object-cover mb-4 rounded-md"
              />
              <h3 className="text-xl font-semibold text-[#3E2F2F] mb-2">
                {service.title}
              </h3>
              <p className="text-[#6B4F4F] text-md">{service.description}</p>
            </motion.div>
          ))}
        </div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: services.length * 0.1 + 0.3 }}
          viewport={{ once: true }}
          className="mt-12 flex justify-center"
        >
          <Link
            href="#contact"
            className="inline-flex items-center gap-3 px-8 py-4 bg-[#A65D5D] text-white font-semibold rounded-lg shadow-md hover:bg-[#8C4A4A] transition duration-300"
          >
            <span>Schedule Your Consultation</span>
            <motion.div
              animate={{
                y: [0, -8, 0], // bounce up and down
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <ArrowDown className="w-6 h-6" />
            </motion.div>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
